import { useContext, useState, useEffect, useMemo, Suspense } from "react";
import { Storage } from "aws-amplify";
import ProductContext from "../../contexts/ProductContext";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { MediaUri, ProductStatus, sortOrders } from "../../constants/enum";
import { FcDownload } from "react-icons/fc";
import { downloadResolutionOptions } from "../../constants/enum";
import { FaDownload } from "react-icons/fa";
import ModalConfirm from "../../components/ModalConfirm";
import { downloadBlob } from "../../helpers/download";
import {
  compareImageFileName,
  getFileNameFromPath,
} from "../../helpers/sortImage";
import Footer from "../../components/Footer";
import { API, graphqlOperation } from "aws-amplify";
import { searchProducts } from "../../graphql/queries";
import wrapPromise from "../../helpers/wrapPromise";
import { extractSearchProductsV2 } from "../../helpers/extract";
import Card from "../../components/Card";
import Waiting from "../../components/Waiting";
import Loading from "../../components/Loading";
import InfoCard from "./infoCard";
import PkCsPhotoGallery, { usePkCsPhotoImages } from "./pkCsPhotoGallery";
import { NoImageBox } from "./imagePlaceholder";
import Select from "react-select";

const productTabs = {
  EA: "EA",
  PACK: "PACK",
  CASE: "CASE",
  PK_CS_PHOTO: "PK_CS_PHOTO",
};

/**
 * slug แยกจาก value/label เพราะเอาไปประกอบเป็น id ของ dom (tabs-{slug}3 / tabs-{slug}-tab3)
 * ที่ panel ฝั่งล่างเขียนไว้ตรง ๆ ถ้าไปสร้างจาก value หรือ label
 * พอเปลี่ยนชื่อ tab ทีนึง aria จะหลุดจากกันทันที (label มี / กับเว้นวรรค ใช้เป็น id ไม่ได้ด้วย)
 */
const productTabList = [
  { value: productTabs.EA, slug: "ea", label: "EA" },
  { value: productTabs.PACK, slug: "pack", label: "PACK" },
  { value: productTabs.CASE, slug: "case", label: "CASE" },
  {
    value: productTabs.PK_CS_PHOTO,
    slug: "pkcsphoto",
    label: "PK / CS (Photo)",
  },
];

const TAB_CLASS =
  "my-2 block cursor-pointer border-x-0 border-b-2 border-t-0 border-transparent px-7 pb-3.5 pt-4 text-xs font-medium uppercase leading-tight text-neutral-500 hover:isolate hover:border-transparent hover:bg-neutral-100 focus:isolate focus:border-transparent data-[te-nav-active]:border-primary data-[te-nav-active]:text-primary dark:text-neutral-400 dark:hover:bg-transparent dark:data-[te-nav-active]:border-primary-400 dark:data-[te-nav-active]:text-primary-400";

/**
 * กล่องของ panel ฝั่งขวาตอนยังไม่มีเนื้อหา (loading / ไม่มีข้อมูล)
 * ใช้ร่วมกันใน tab pack, case, PK / CS (Photo) — ตัว ea ไม่ใช้เพราะมีข้อมูลจาก context เสมอ
 * ล็อกความสูงไว้เท่ากันเพื่อไม่ให้ layout หุบหรือกระโดดตอนสลับ tab
 */
const InfoPlaceholder = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-row items-center justify-center w-full min-h-[24rem] lg:min-h-[32rem]">
    {children}
  </div>
);

const EmptyInfo = ({ text }: { text: string }) => (
  <InfoPlaceholder>
    <span className="text-xl font-bold text-gray-400">{text}</span>
  </InfoPlaceholder>
);

// ใช้ Loading ไม่ใช่ Waiting เพราะ Waiting เป็น full-page loader (min-h-screen + absolute)
const LoadingInfo = () => (
  <InfoPlaceholder>
    <Loading />
  </InfoPlaceholder>
);

const ACTIVE_STATUSES = [
  ProductStatus.Launch,
  ProductStatus.Normal,
  ProductStatus.TobeDiscontinued,
];

function SingleProduct() {
  const { product } = useContext(ProductContext);
  const productInfo: any = product;
  const mainImages = product ? product["files"] : [];
  const [galleryImages, setGalleryImages] = useState<any>([]);
  const [activeTab, setActiveTab] = useState(productTabs.EA);

  /** panel ที่ไม่ active ซ่อนด้วย hidden ตัวที่ active ค่อย fade เข้ามา */
  const getPanelClass = (tab: string): string => {
    return activeTab === tab
      ? "block opacity-100 transition-opacity duration-150 ease-linear"
      : "hidden opacity-0";
  };

  // ยิง s3 เฉพาะหลังกด tab PK / CS (Photo) ครั้งแรก (ไม่ยิงตอนโหลดหน้า) แล้ว cache ไว้
  const [pkCsPhotoVisited, setPkCsPhotoVisited] = useState(false);
  const pkCsPhoto = usePkCsPhotoImages(
    pkCsPhotoVisited ? productInfo?.materialCode : undefined
  );
  const hasPkCsPhoto = pkCsPhoto.images.length > 0;
  const [showDialog, setShowDialog] = useState(false);
  const [downloadResolution, setDownloadResolution] = useState(
    downloadResolutionOptions[0].value
  );

  useEffect(() => {
    const signImage = async () => {
      if (mainImages.length > 0) {
        const mediaEndpoints = mainImages.map((mi: any) => {
          return { ...mi };
        });

        const sortMedia = mediaEndpoints
          .map((d: any) => {
            const typeSub = d.mediaTypeSub ? d.mediaTypeSub : "OTHER";
            const imgIndex = sortOrders.indexOf(typeSub.toUpperCase());

            return {
              ...d,
              sortOrder:
                imgIndex || imgIndex === 0 ? imgIndex : sortOrders.length,
            };
          })
          .sort((a: any, b: any) => {
            const imgA = a.sortOrder ? a.sortOrder : 0;
            const imgB = b.sortOrder ? b.sortOrder : 0;
            if (imgA !== imgB) {
              return imgA - imgB;
            }

            // mediaTypeSub เดียวกัน -> เรียงด้วย logic เดียวกับ PK / CS (Photo)
            // ใช้ path เพราะ field name เก็บชื่อ product (ซ้ำกันทุกไฟล์) ไม่ใช่ชื่อไฟล์
            return compareImageFileName(
              getFileNameFromPath(a.path),
              getFileNameFromPath(b.path)
            );
          });

        await setGalleryImages(sortMedia);
      }
    };
    if (mainImages) {
      signImage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // memo ไว้ไม่ให้ items เปลี่ยน reference ทุก render -> ImageGallery ไม่ต้อง re-render ตอนกดเปลี่ยน tab
  const mainImageObjects = useMemo(
    () =>
      galleryImages.map((img: any, index: number) => ({
        original: `${MediaUri}/display/${img.path}`,
        thumbnail: `${MediaUri}/thumbnail/${img.path}`,
        description: `${img.name}`,

        renderItem: () => {
          return (
            <div className="flex flex-row justify-center">
              <div className="px-8 py-4">
                <div className="relative" data-te-dropdown-ref>
                  <button
                    className="float-left p-2 bg-white rounded-full shadow-lg hover:bg-slate-400 shadow-slate-400"
                    type="button"
                    id={`dropdownMenuButton${index}`}
                    data-te-dropdown-toggle-ref
                    aria-expanded="false"
                    data-te-ripple-init
                    data-te-ripple-color="light"
                  >
                    <FcDownload size={25} />
                  </button>
                  <ul
                    className="absolute z-[1000] float-left m-0 hidden min-w-max list-none overflow-hidden rounded-lg border-none bg-white bg-clip-padding text-left text-base shadow-lg dark:bg-neutral-700 [&[data-te-dropdown-show]]:block"
                    aria-labelledby={`dropdownMenuButton${index}`}
                    data-te-dropdown-menu-ref
                  >
                    {downloadResolutionOptions.map((op: any) => {
                      return (
                        <li key={op.value}>
                          <button
                            className="block w-full px-4 py-2 text-sm font-bold bg-transparent whitespace-nowrap text-neutral-700 hover:bg-neutral-100 active:text-neutral-800 active:no-underline disabled:pointer-events-none disabled:bg-transparent disabled:text-neutral-400 dark:text-neutral-200 dark:hover:bg-neutral-600"
                            data-te-dropdown-item-ref
                            onClick={async () => {
                              let source =
                                op.value === "HIGH"
                                  ? "downloads/large"
                                  : op.value === "MEDIUM"
                                    ? "downloads/medium"
                                    : op.value === "LOW"
                                      ? "downloads/small"
                                      : "original";

                              const result = await Storage.get(
                                `${source}/${img.path}`,
                                {
                                  download: true,
                                  // contentType: "image/png"
                                }
                              );

                              // const result = await Storage.get(key, { download: true });
                              downloadBlob(
                                result.Body,
                                `${op.value}-${img.mediaType}-${img.mediaTypeSub}-${img.name ? img.name.replace(".", "") : "_.png"}`
                              );
                            }}
                          >
                            <span>{`${op.label}`}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <img
                  className="object-contain w-48 rounded-t-lg h-fit md:w-96 "
                  src={`${MediaUri}/display/${img.path}`}
                  alt={`${img.name}`}
                  onError={(e: any) => {
                    e.target.src = "/brands/no_image.png";
                  }}
                />
              </div>
            </div>
          );
        },
      })),
    [galleryImages]
  );

  const fetchProductSeries = () => {
    const products: any = API.graphql(
      graphqlOperation(searchProducts, {
        brandType: productInfo.brandType,
        productHierachy: `${productInfo.productHierachy.substring(0, 13)}0`, // last digit of product hierachy = 0.
        packaging: "EA",
        statuses: ACTIVE_STATUSES,
        limit: 25,
        from: 0,
      })
    );

    return {
      products: wrapPromise(products),
    };
  };

  const pds = fetchProductSeries();

  const ShowProductSerires = ({ pds }: any) => {
    const products = pds.products.read();
    const productSeries = extractSearchProductsV2(products);
    if (!productSeries || productSeries.length === 0) {
      return (
        <div className="flex flex-row justify-center">
          <span className="text-xl font-bold text-gray-400">
            No ea information.
          </span>
        </div>
      );
    }

    return (
      <div className="px-8 py-4">
        <h2 className="text-xl">Product Serie(s)</h2>
        <div>
          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {productSeries.map((sr: any) => {
              if (productInfo.id === sr.value.id) {
                return (
                  <div key={sr.value.id}>
                    <Card data={sr.value} />
                  </div>
                );
              }
              return (
                <div key={sr.value.id}>
                  <Card data={sr.value} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const fetchProductPack = () => {
    const product: any = API.graphql(
      graphqlOperation(searchProducts, {
        brandType: productInfo.brandType,
        materialCode: productInfo.materialCode,
        productHierachy: productInfo.productHierachy,
        packaging: "PAC",
        limit: 1,
        from: 0,
      })
    );

    return {
      product: wrapPromise(product),
    };
  };
  const pdp = fetchProductPack();

  const ShowProductPack = ({ pdp }: any) => {
    const product = pdp.product.read();
    const productPack = extractSearchProductsV2(product);
    if (!productPack || productPack.length === 0) {
      return <EmptyInfo text="No pack information." />; //go to un auth page.
    }

    const productInfoCard = productPack.map((info: any) => {
      const value = info.value;

      return {
        ...value,
        tags: JSON.parse(value.tags),
        functions: JSON.parse(value.functions),
      };
    });

    return <InfoCard info={productInfoCard[0]} collapseId="collapsePack" />;
  };

  const fetchProductCase = () => {
    const product: any = API.graphql(
      graphqlOperation(searchProducts, {
        materialCode: productInfo.materialCode,
        productHierachy: productInfo.productHierachy,
        packaging: "CS",
        limit: 1,
        from: 0,
      })
    );

    return {
      product: wrapPromise(product),
    };
  };
  const pdc = fetchProductCase();

  const ShowProductCase = ({ pdc }: any) => {
    const product = pdc.product.read();
    const productCase = extractSearchProductsV2(product);
    if (!productCase || productCase.length === 0) {
      return <EmptyInfo text="No case information." />; //go to un auth page.
    }

    const productInfoCard = productCase.map((info: any) => {
      const value = info.value;

      return {
        ...value,
        tags: JSON.parse(value.tags),
        functions: JSON.parse(value.functions),
      };
    });

    return <InfoCard info={productInfoCard[0]} collapseId="collapseCase" />;
  };

  return (
    <>
      <ModalConfirm
        show={showDialog}
        onHide={() => {}}
        title="Download file(s)"
        cancelText={"Close"}
        cancelAction={async () => {
          await setShowDialog(false);
        }}
        okClass="btn-submit"
        okText="Download"
        okAction={async () => {
          let source =
            downloadResolution === "HIGH"
              ? "downloads/large"
              : downloadResolution === "MEDIUM"
                ? "downloads/medium"
                : downloadResolution === "LOW"
                  ? "downloads/small"
                  : "original";

          await productInfo.files.map(async (fl: any) => {
            const key = `${source}/${fl.path}`;
            const result = await Storage.get(key, {
              download: true,
              // contentType: "image/png",
            });
            downloadBlob(
              result.Body,
              `${downloadResolution}-${fl.mediaType}-${fl.mediaTypeSub}-${fl.name ? fl.name.replace(".", "") : "_.png"}`
            );
          });
          await setShowDialog(false);
        }}
        maxWidth="max-w-4xl"
      >
        {showDialog ? (
          <div className="overflow-auto h-96">
            <div className="flex flex-row justify-center px-4 py-2">
              <div className="w-full">
                <Select
                  options={downloadResolutionOptions}
                  defaultValue={downloadResolutionOptions[0]}
                  onChange={(e: any) => {
                    setDownloadResolution(e.value);
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {productInfo.files.map((file: any) => {
                return (
                  <div key={file.id} className="flex flex-row m-2">
                    <img
                      className="object-contain h-24"
                      src={`${MediaUri}/thumbnail/${file.path}`}
                      alt={`${file.name}`}
                    />
                    <div className="grid justify-between grid-cols-1">
                      <span className="font-bold">{file.name}</span>
                      <div className="grid grid-cols-1 gap-0">
                        <span className="text-xs">{file.mediaType}</span>
                        <span className="text-xs">{file.mediaTypeSub}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-row justify-center">
            <span>No Data</span>
          </div>
        )}
      </ModalConfirm>
      <div className="grid grid-cols-1 p-1 m-1 lg:p-4 lg:m-4 md:grid-cols-2">
        {activeTab === productTabs.PK_CS_PHOTO ? (
          <PkCsPhotoGallery
            images={pkCsPhoto.images}
            loading={pkCsPhoto.loading}
          />
        ) : productInfo.files.length > 0 ? (
          <div className="flex flex-row justify-center">
            <div className="object-contain w-4/5 py-4 h-fit">
              <ImageGallery
                items={mainImageObjects}
                infinite={false}
                showBullets={true}
                showIndex={true}
                showFullscreenButton={true}
                showPlayButton={false}
                onErrorImageURL="/brands/no_image.png"
              />
              <div className="flex flex-row justify-center mt-8">
                <button
                  type="button"
                  onClick={() => {
                    setShowDialog(true);
                  }}
                  disabled={productInfo.files.length === 0}
                  data-te-ripple-init
                  data-te-ripple-color="light"
                  className="disabled:bg-slate-500  inline-block rounded bg-primary px-6 pb-2 pt-2.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-primary-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-primary-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-primary-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] dark:shadow-[0_4px_9px_-4px_rgba(59,113,202,0.5)] dark:hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)] dark:active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.2),0_4px_18px_0_rgba(59,113,202,0.1)]"
                >
                  <span className="flex flex-row justify-center gap-2">
                    <FaDownload size={15} /> <span> Download all file(s)</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <NoImageBox />
        )}

        <div className="px-4 py-4 my-8 bg-gray-100 rounded shadow-lg lg:py-8 lg:px-10 md:my-2">
          <ul
            className="flex flex-row flex-wrap pl-0 mb-4 list-none border-b-0"
            id="tabs-tab3"
            role="tablist"
          >
            {productTabList.map((tab) => (
              <li role="presentation" key={tab.value}>
                <button
                  type="button"
                  className={TAB_CLASS}
                  id={`tabs-${tab.slug}-tab3`}
                  role="tab"
                  aria-controls={`tabs-${tab.slug}3`}
                  aria-selected={activeTab === tab.value}
                  // ใช้ data attribute ชื่อเดิมคู่กับ variant data-[te-nav-active] ที่ class ของธีมใช้อยู่
                  {...(activeTab === tab.value
                    ? { "data-te-nav-active": true }
                    : {})}
                  onClick={() => {
                    setActiveTab(tab.value);
                    if (tab.value === productTabs.PK_CS_PHOTO) {
                      setPkCsPhotoVisited(true);
                    }
                  }}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          <div>
            <div
              className={getPanelClass(productTabs.EA)}
              id="tabs-ea3"
              role="tabpanel"
              aria-labelledby="tabs-ea-tab3"
            >
              <InfoCard info={productInfo} collapseId="collapseEa" />
            </div>
            <div
              className={getPanelClass(productTabs.PACK)}
              id="tabs-pack3"
              role="tabpanel"
              aria-labelledby="tabs-pack-tab3"
            >
              <Suspense fallback={<LoadingInfo />}>
                <ShowProductPack pdp={pdp} />
              </Suspense>
            </div>
            <div
              className={getPanelClass(productTabs.CASE)}
              id="tabs-case3"
              role="tabpanel"
              aria-labelledby="tabs-case-tab3"
            >
              <Suspense fallback={<LoadingInfo />}>
                <ShowProductCase pdc={pdc} />
              </Suspense>
            </div>
            <div
              className={getPanelClass(productTabs.PK_CS_PHOTO)}
              id="tabs-pkcsphoto3"
              role="tabpanel"
              aria-labelledby="tabs-pkcsphoto-tab3"
            >
              {pkCsPhoto.loading ? (
                <LoadingInfo />
              ) : hasPkCsPhoto ? (
                <InfoCard
                  info={productInfo}
                  showPackagingInfo={false}
                  collapseId="collapsePkCsPhoto"
                />
              ) : (
                <EmptyInfo text="No PK / CS (Photo) information." />
              )}
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={<Waiting />}>
        <ShowProductSerires pds={pds} />
      </Suspense>

      <Footer />
    </>
  );
}

export default SingleProduct;
